import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, MapPin, Train } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import type { BangkokDistrict } from "@/content/bangkok/districts";
import type { CoreServicePillar } from "@/content/service-pillars/core.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { getBangkokDistrictCoords } from "@/lib/geo-coords";

export const Route = createFileRoute("/bangkok/$district/")({
  loader: async ({
    params,
  }): Promise<{ district: BangkokDistrict; services: CoreServicePillar[] }> => {
    const { BANGKOK_CORE_SERVICES, getDistrict } = await import(
      "@/content/bangkok/districts"
    );
    const d = getDistrict(params.district);
    if (!d) throw notFound();
    const { getCoreServicePillar } = await import(
      "@/content/service-pillars/core.gen"
    );
    const services = BANGKOK_CORE_SERVICES.map((s) =>
      getCoreServicePillar(s),
    ).filter(Boolean) as CoreServicePillar[];
    return { district: d, services };
  },
  head: ({ params, loaderData }) => {
    const d = loaderData?.district;
    const site = getActiveSiteUrl();
    const coords = d ? getBangkokDistrictCoords(d.slug) : undefined;
    if (!d) {
      return pageHeadForProvince({
        title: "ไม่พบเขต",
        description: "ไม่พบเขตนี้",
        path: `/bangkok/${params.district}`,
      });
    }
    const rawTitle = `Notary ${d.nameTh} — แปล, MFA, วีซ่า | กรุงเทพ`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `บริการ Notary, แปลรับรอง, MFA และวีซ่า on-site ในเขต${d.nameTh} ใกล้ ${d.transit.slice(0, 2).join(", ")} — รับเคสด่วนวันเดียวกัน`;
    const desc = rawDesc.length > 158 ? rawDesc.slice(0, 157) + "…" : rawDesc.length < 120 ? `${rawDesc}. ทีม Notarial Services Attorney + เดินเอกสาร MFA แจ้งวัฒนะให้ครบจบ.`.slice(0, 158) : rawDesc;
    const base = pageHeadForProvince({ title, description: desc, path: `/bangkok/${d.slug}` });
    const faqs = [
      {
        q: `ทีมเดินทางถึง${d.nameTh}ภายในกี่ชั่วโมง?`,
        a: `รับเคส on-site ใกล้ ${d.transit.slice(0, 2).join(", ")} — แจ้งก่อนเที่ยงทีมถึงในบ่ายวันเดียวกัน`,
      },
      {
        q: `รับเคสที่คอนโด/โรงแรมในเขต${d.nameTh}ได้ไหม?`,
        a: `ได้ครับ ทนายและทีมแปลเดินทางพร้อมอุปกรณ์รับรองครบชุด ใช้พื้นที่ล็อบบี้คอนโดหรือห้องประชุมโรงแรมเซ็นเอกสารต่อหน้าได้ทันที`,
      },
      {
        q: `ในเขต${d.nameTh}มีบริการอะไรบ้าง?`,
        a: `รับเคส Notary Public, แปลรับรอง 30+ ภาษา, MFA Consular Legalization, สถานทูตทุกประเทศ, ตรวจประวัติอาชญากรรม และวีซ่าไทย/ต่างประเทศ`,
      },
    ];
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "พื้นที่กรุงเทพ", url: `${site}/bangkok` },
              { name: d.nameTh, url: `${site}/bangkok/${d.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "@id": `${site}/bangkok/${d.slug}#place`,
            name: `${d.nameTh} (${d.nameEn})`,
            description: d.intro,
            address: {
              "@type": "PostalAddress",
              addressLocality: d.nameEn,
              addressRegion: "Bangkok",
              postalCode: d.postalCodes[0],
              addressCountry: "TH",
            },
            ...(coords
              ? {
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: coords.lat,
                    longitude: coords.lng,
                  },
                }
              : {}),
            containedInPlace: { "@type": "City", name: "Bangkok" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/bangkok/${d.slug}#service`,
            name: `Notary, Translation, MFA & Visa — ${d.nameEn}, Bangkok`,
            provider: { "@id": `${site}/#organization` },
            areaServed: {
              "@type": "AdministrativeArea",
              name: `${d.nameEn} District, Bangkok`,
            },
            url: `${site}/bangkok/${d.slug}`,
          }),
        },
        ...(coords
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "LocalBusiness",
                  "@id": `${site}/bangkok/${d.slug}#localbusiness`,
                  name: `Thai Notary Bangkok — ${d.nameEn}`,
                  image: `${site}/og-image.jpg`,
                  url: `${site}/bangkok/${d.slug}`,
                  telephone: COMPANY.phone,
                  priceRange: "฿฿",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: d.nameEn,
                    addressRegion: "Bangkok",
                    postalCode: d.postalCodes[0],
                    addressCountry: "TH",
                  },
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: coords.lat,
                    longitude: coords.lng,
                  },
                  areaServed: {
                    "@type": "AdministrativeArea",
                    name: `${d.nameEn} District, Bangkok`,
                  },
                  serviceArea: {
                    "@type": "GeoCircle",
                    geoMidpoint: {
                      "@type": "GeoCoordinates",
                      latitude: coords.lat,
                      longitude: coords.lng,
                    },
                    geoRadius: 5000,
                  },
                }),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/bangkok/${params.district}`)) },
      ],
    };
  },
  component: DistrictHub,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบเขตนี้</h1>
        <Link to="/bangkok" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการเขต
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function DistrictHub() {
  const { district: d, services } = Route.useLoaderData() as {
    district: BangkokDistrict;
    services: CoreServicePillar[];
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/bangkok" className="hover:text-primary">กรุงเทพ</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{d.nameTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              {d.postalCodes.join(" / ")} • {d.nameEn}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              Notary Public, แปลรับรอง, MFA & วีซ่า — {d.nameTh}
            </h1>
            <p
              id="ai-answer-tldr"
              className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed"
            >
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                สรุปสั้น
              </span>
              {d.intro} ทีม Thai Notary รับเคส on-site ใกล้ {d.transit.slice(0, 2).join(", ")} — ทนาย Notarial Services Attorney + ทีมแปลเดินทางถึงคอนโด/สำนักงาน/โรงแรมในเขต{d.nameTh}ภายในวันเดียวกัน.
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                <Train className="mr-1 inline h-3.5 w-3.5 text-gold" /> สถานีใกล้
              </div>
              <div className="mt-1 text-sm font-bold">{d.transit.join(", ")}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                <MapPin className="mr-1 inline h-3.5 w-3.5 text-gold" /> Landmark
              </div>
              <div className="mt-1 text-sm font-bold">{d.landmarks.join(", ")}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                ความหนาแน่น expat
              </div>
              <div className="mt-1 text-sm font-bold">
                {d.expatDensity === "very-high"
                  ? "สูงมาก (Top tier)"
                  : d.expatDensity === "high"
                  ? "สูง"
                  : d.expatDensity === "medium"
                  ? "ปานกลาง"
                  : "ต่ำ"}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">บริการในเขต {d.nameTh}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              เลือกบริการเพื่อดูขั้นตอน เอกสาร และราคาเฉพาะสำหรับเขต{d.nameTh}
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to="/bangkok/$district/$service"
                  params={{ district: d.slug, service: s.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <h3 className="text-base font-bold text-primary group-hover:text-gold">
                    {s.h1.length > 60 ? s.h1.slice(0, 58) + "…" : s.h1}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                    {s.metaDesc}
                  </p>
                  {s.pricing[0]?.price && (
                    <div className="mt-3 text-sm font-bold text-gold">
                      เริ่ม {s.pricing[0].price}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ทำไมเลือกเรา สำหรับเคสในเขต {d.nameTh}</h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {[
                `ทีมทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — รับรองได้ถูกต้องตามที่ MFA และสถานทูตทุกประเทศยอมรับ`,
                `รับเคส on-site ที่คอนโด/สำนักงาน/โรงแรมใกล้ ${d.transit.slice(0, 2).join(", ")}`,
                `ทีมแปลครอบคลุม 30+ ภาษา — ส่งงานทันทีพร้อมแนบ Translation Certificate`,
                `เดินเอกสาร MFA Consular (แจ้งวัฒนะ) และสถานทูตทุกประเทศในกรุงเทพให้ครบจบ`,
                `รองรับเคสด่วนภายในวันเดียวกัน — มีลูกค้า expat และบริษัทในย่าน${d.nameTh}ใช้บริการต่อเนื่อง (ขึ้นกับคิวและเวลาที่ได้รับเอกสาร)`,
                `${COMPANY.trustLine} — ปลอดภัย โปร่งใส ใบเสร็จเต็มรูปแบบ`,
              ].map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">
              พร้อมเริ่มเคสที่{d.nameTh} แล้วใช่ไหม?
            </h2>
            <p className="mt-3 text-sm opacity-90">
              ทักไลน์ส่งภาพเอกสารให้ทีมประเมินภายใน 30 นาที — รับเคส on-site ทุกวันรวมเสาร์–อาทิตย์
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground"
              >
                โทร {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold"
              >
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
